'use client';

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Link } from '@/components/shared/Link';
import { PageHero } from '@/components/shared/PageHero';
import { SectionLabel } from '@/components/shared/SectionLabel';
import { ServiceCard } from '@/components/shared/ServiceCard';
import { CheckCircle2 } from 'lucide-react';
import { useTranslations } from '@/i18n/intl';
import { UploadedImage } from '@/components/shared/UploadedImage';
import { ServiceContactForm } from '@/components/services/ServiceContactForm';
import { stripHtml, parseJsonField } from '@/lib/utils';
import { RichText } from '@/components/shared/RichText';
import { getServiceBySlug } from '@/lib/actions/services';
import NotFoundPage from './NotFoundPage';

export default function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const t = useTranslations('Expertise');
  const tCommon = useTranslations('Common');
  const [service, setService] = useState<any>(null);
  const [otherServices, setOtherServices] = useState<any[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!slug) return;
    void getServiceBySlug(slug).then((data) => {
      if (!data?.service) {
        setNotFound(true);
        setReady(true);
        return;
      }
      const merged = [...(data.otherServices || [])];
      if (merged.length < 3 && data.additionalServices?.length) {
        merged.push(...data.additionalServices);
      }
      setService(data.service);
      setOtherServices(merged);
      setReady(true);
    });
  }, [slug]);

  if (!ready) return null;
  if (notFound || !service) return <NotFoundPage />;

  const individualAssessments = parseJsonField(service.individualAssessments, []);
  const organizationalAssessments = parseJsonField(service.organizationalAssessments, []);
  const steps = parseJsonField(service.steps, []);
  const modules = parseJsonField(service.modules, []);
  const scopeFramework = parseJsonField(service.scopeFramework, []);
  const outputs = parseJsonField(service.outputs, []);
  const insights = service.insights || [];

  return (
    <>
      <Helmet>
        <title>{service.title}</title>
        <meta name="description" content={stripHtml(service.shortDescription || '')} />
        <meta property="og:title" content={service.title} />
        <meta property="og:description" content={stripHtml(service.shortDescription || '')} />
        {service.image && <meta property="og:image" content={service.image} />}
      </Helmet>
      <PageHero
        title={service.title}
        subtitle={service.category?.name || service.shortDescription}
        breadcrumbs={[
          { label: tCommon('home'), href: '/' },
          { label: tCommon('expertise') },
          { label: service.title },
        ]}
      />

      {service.image && (
        <section className="bg-white px-5 md:px-[40px] pt-10">
          <div className="max-w-[1170px] mx-auto">
            <div className="aspect-[21/9] relative rounded-2xl overflow-hidden shadow-2xl border border-light">
              <UploadedImage
                src={service.image}
                alt={service.title}
                fill
                className="object-cover"
                priority
              />
            </div>
          </div>
        </section>
      )}

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
        <div className="max-w-[1170px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="mb-12">
              <h2
                className="text-[32px] md:text-[40px] font-normal text-charcoal leading-tight mb-6 font-heading"
              >
                {t('overview')}
              </h2>
              <RichText
                html={service.longDescription || ''}
                className="text-[16px] text-gray leading-relaxed font-sans [&_p]:mb-4"
              />
            </div>

            {(individualAssessments.length > 0 || organizationalAssessments.length > 0) && (
              <div className="mb-12">
                <h3 className="text-[24px] font-bold text-navy mb-8 font-heading">
                  {t('assessmentApproach')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {individualAssessments.length > 0 && (
                    <div className="bg-light/30 p-6 rounded-lg border border-light">
                      <h4 className="text-[18px] font-bold text-navy mb-4 font-heading">{t('individualAssessments')}</h4>
                      <ul className="space-y-3">
                        {individualAssessments.map((item: string) => (
                          <li key={item} className="flex items-center gap-2 text-[15px] text-gray">
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {organizationalAssessments.length > 0 && (
                    <div className="bg-light/30 p-6 rounded-lg border border-light">
                      <h4 className="text-[18px] font-bold text-navy mb-4 font-heading">{t('organizationalAssessments')}</h4>
                      <ul className="space-y-3">
                        {organizationalAssessments.map((item: string) => (
                          <li key={item} className="flex items-center gap-2 text-[15px] text-gray">
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {steps.length > 0 && (
              <div className="mb-12">
                <h3 className="text-[24px] font-bold text-navy mb-8 font-heading">
                  {t('alignmentProcess')}
                </h3>
                <div className="space-y-6">
                  {steps.map((step: any, index: number) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-navy text-white rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="text-[18px] font-bold text-navy mb-1 font-heading">{step.title}</h4>
                        <RichText
                          html={step.description || ''}
                          className="text-[15px] text-gray leading-relaxed font-sans [&_p]:mb-4"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {scopeFramework.length > 0 && (
              <div className="mb-12">
                <h3 className="text-[24px] font-bold text-navy mb-8 font-heading">
                  SCOPE Framework™
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                  {scopeFramework.map((item: any, index: number) => (
                    <div key={index} className="p-4 bg-light/30 rounded-lg border border-light flex flex-col items-center justify-center aspect-square">
                      <span className="text-[14px] font-bold text-navy font-heading">{item.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {modules.length > 0 && (
              <div className="mb-12">
                <h3 className="text-[24px] font-bold text-navy mb-8 font-heading">
                  {t('programModules')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                  {modules.map((module: string) => (
                    <div key={module} className="flex items-center gap-2 text-[15px] text-gray py-2 border-b border-light/50 font-sans">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full shrink-0" />
                      {module}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {outputs.length > 0 && (
              <div className="mb-12 bg-navy p-8 rounded-lg text-white">
                <h3 className="text-[20px] font-bold mb-6 font-heading">
                  {t('expectedOutputs')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {outputs.map((output: string) => (
                    <div key={output} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span className="text-[15px] font-sans">{output}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {insights.length > 0 && (
              <div className="mt-16 pt-16 border-t border-light">
                <h3 className="text-[24px] font-bold text-navy mb-8 font-heading">
                  {t('relatedInsights')}
                </h3>
                <div className="space-y-6">
                  {insights.map((insight: any) => (
                    <Link
                      key={insight.id}
                      href={`/insights/${insight.slug}`}
                      className="group flex gap-6 p-4 rounded-xl border border-transparent hover:border-light hover:bg-light/30 transition-all"
                    >
                      {insight.coverImage && (
                        <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-lg overflow-hidden border border-light">
                          <img
                            src={insight.coverImage}
                            alt={insight.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="flex-1 py-1">
                        <span className="text-[10px] font-bold text-primary tracking-widest uppercase block mb-2">
                          {insight.serviceCategory?.name || tCommon('insights')}
                        </span>
                        <h4 className="text-[18px] md:text-[20px] font-bold text-navy leading-tight group-hover:text-primary transition-colors">
                          {insight.title}
                        </h4>
                        <p className="text-[13px] text-gray mt-2 line-clamp-2 hidden md:block">
                          {stripHtml(insight.content || '')}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <ServiceContactForm serviceTitle={service.title} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-light/30">
        <div className="max-w-[1170px] mx-auto">
          <SectionLabel text={tCommon('expertise')} />
          <h2
            className="text-[32px] md:text-[40px] font-normal text-navy leading-snug mb-12 font-heading"
          >
            {t('exploreOther')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[24px]">
            {otherServices.map((s) => (
              <ServiceCard
                key={s.slug}
                title={s.title}
                description={s.shortDescription}
                href={`/services/${s.slug}`}
                image={s.homeImage || s.image}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
